import { useEffect, useMemo } from "react";
import { LoaderFunction, useNavigate } from "react-router-dom";

import { useConfetti } from "@/hooks";
import { Button } from "atoms/button/Button";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import { loadIfFlag } from "services/Flags";

enum EligibilityStatus {
  Eligible = "Eligible",
  NotEligible = "Not Eligible",
}

const disqualifyingQuestionsAnswers = {
  Diagnoses: [
    "Pregnant",
    "Breastfeeding",
    "KidneyDisease",
    "LiverDisease",
    "EatingDisorder",
    "SuicidalThoughts",
    "Cancer",
    "OrganTransplant",
    "GastrointestinalCondition",
  ],
  MedicalHistory: [
    "Type2DiabetesYesInsulin",
    "Type1Diabetes",
    "DiabeticRetinopathy",
    "Pancreatitis",
    "ThyroidCystOrCancer",
  ],
  WeightLossMedsThroughUs: ["No"],
};

// This function checks the eligibility of a patient based on their answers to the GLP questionnaire.
// GLP questionnaire is stored in local storage as a JSON object.
const checkEligibility = (): EligibilityStatus => {
  const answers = localStorage.getItem("glpQuestionnaire") ?? "{}";
  const answersObj = JSON.parse(answers);

  let isEligible = EligibilityStatus.Eligible;

  // Per documentation, we calculate BMI and if it is less than 27, the patient is not eligible
  const bmi = calculateBMI(
    parseInt(answersObj.Height),
    parseInt(answersObj.Inches),
    parseInt(answersObj.Weight)
  );
  if (bmi < 27) {
    isEligible = EligibilityStatus.NotEligible;
    return isEligible;
  }

  // Check if any of the answers are disqualifying
  Object.keys(disqualifyingQuestionsAnswers).forEach((key) => {
    if (!answersObj[key]) {
      return;
    }

    // There are two types of answers: strings and arrays of strings
    // If the answer is a string, we check if this answer is in the disqualifying list
    if (typeof answersObj[key] === "string") {
      if (
        disqualifyingQuestionsAnswers[
          key as keyof typeof disqualifyingQuestionsAnswers
        ].includes(answersObj[key])
      ) {
        isEligible = EligibilityStatus.NotEligible;
      }
    } else {
      // If the answer is an array, we check if any of the answers are in the disqualifying list
      answersObj[key].forEach((answer: string) => {
        if (
          disqualifyingQuestionsAnswers[
            key as keyof typeof disqualifyingQuestionsAnswers
          ].includes(answer)
        ) {
          isEligible = EligibilityStatus.NotEligible;
        }
      });
    }
  });

  return isEligible;
};

const calculateBMI = (feet: number, inches: number, weight: number): number => {
  const height = feet * 12 + inches;
  const bmi = (weight * 703) / (height * height);
  return isNaN(bmi) ? 0 : bmi;
};

export const glpConfirmationLoader: LoaderFunction = loadIfFlag("repeatmd-glp")(
  async () => {
    return null;
  }
);

const GLPConfirmation = () => {
  const { triggerConfetti } = useConfetti();
  const navigate = useNavigate();

  const handlePrev = () => {
    navigate("/health/glp/questionnaire");
  };

  const handleProceed = () => {
    if (isEligible === EligibilityStatus.Eligible) {
      navigate("/shop/weight-loss-programs");
      return;
    }
    localStorage.removeItem("glpQuestionnaire");
    localStorage.removeItem("currentStep");
    localStorage.removeItem("subStep");
    localStorage.removeItem("isSubStep");
    localStorage.removeItem("progress");
    navigate("/");
  };

  const isEligible = useMemo(() => checkEligibility(), []);

  useEffect(() => {
    if (isEligible === EligibilityStatus.Eligible) {
      triggerConfetti();
    }
  }, [isEligible]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex w-full items-center justify-between gap-3 border-b-2 border-gray-50 bg-white bg-opacity-95 pt-[env(safe-area-inset-top)]">
        <div className="flex items-center">
          <IconButton
            svg={ChevronLeft}
            onClick={handlePrev}
            size="small"
            style="iconOnly"
          />
          <p className="text-md text-bold1">Quiz</p>
        </div>
      </div>
      <div className="flex flex-grow flex-col overflow-hidden pt-10">
        <div className="flex-grow overflow-y-auto px-4 pb-4">
          <div className="flex h-full flex-col gap-6">
            <p className="bg-gradient-to-r from-[#2B5F6A] to-[#54BAD0] bg-clip-text text-center font-sans text-4xl text-transparent">
              Repeat<strong>Health</strong>
            </p>
            <p className="pt-6 font-serif text-h1">
              {eligibilityText[isEligible].title}
            </p>
            <p className="font-serif text-body1">
              {eligibilityText[isEligible].description}
            </p>
            {isEligible === EligibilityStatus.Eligible && (
              <p className="pt-8 text-sm">
                {eligibilityText[isEligible].smallText}
              </p>
            )}
            <div className="mt-auto">
              <Button fullWidth onClick={handleProceed}>
                {eligibilityText[isEligible].buttonText}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GLPConfirmation;

const eligibilityText = {
  [EligibilityStatus.Eligible]: {
    title: "Congrats! You’re qualified to proceed.*",
    description:
      "Next, we’ll collect payment and refer you to our partner for a final consultation by a medical clinician.",
    smallText:
      "*The answers you provided to the quiz indicate that no initial exclusionary criteria screen you out of eligibility for the medications above. A medical clinician will review your answers after checkout to determine if you qualify for a prescription. If the doctor determines you do not qualify, we will refund you within 24 hours at no cost.",
    buttonText: "Proceed to Checkout",
  },
  [EligibilityStatus.NotEligible]: {
    title: "Thank your for completing the quiz.",
    description:
      "Unfortunately, you do not qualify for this program at this time. If you think this is an error, please contact support@repeatmd.com",
    buttonText: "Finish",
  },
};
